
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { useAuthForm } from "@/hooks/useAuthForm";
import { Link } from "react-router-dom";
import { useTranslatedDocumentTitle } from '@/hooks/useDocumentTitle';
import { useCommunity } from '@/contexts/CommunityContext';

const RequestResetPassword = () => {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { handleRequestPasswordReset } = useAuthForm();
  const { currentCommunity } = useCommunity();
  
  // Set page title using the new hook
  useTranslatedDocumentTitle('auth.reset_password.title');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Redirect directly to the reset-password page to simplify the flow
    const redirectUrl = `${window.location.origin}/auth/reset-password`;
    console.log("Password reset redirect URL:", redirectUrl);
    
    const success = await handleRequestPasswordReset(email, redirectUrl);
    
    if (success) {
      // Stay on the page - the toast will inform the user
      setIsProcessing(false);
    } else {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[42%_58%]">
      {/* Left Panel - Brand Section */}
      <div className="hidden md:flex bg-[hsl(var(--primary)/0.03)] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 right-20 w-48 h-48 bg-primary/8 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-10 w-24 h-24 bg-primary/6 rounded-full blur-2xl"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                Secure Your
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
                  {currentCommunity?.name}
                </span>
                <br />
                Account
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-sm">
                Connect with your local community and discover amazing services near you.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Form Section */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card className="rounded-2xl shadow-lg border bg-card">
              <CardHeader className="space-y-4 text-center p-8 pb-6">
                <div className="w-full flex justify-start mb-6">
                  <Link to="/auth" className="text-sm text-muted-foreground hover:underline">
                    &larr; Back to Sign In
                  </Link>
                </div>
                {/* Mobile Brand Header */}
                <div className="md:hidden space-y-2">
                  <span className="text-lg font-semibold text-foreground">
                    {currentCommunity?.name}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    Reset your password
                  </p>
                </div>
                <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
                <CardDescription>
                  Enter your email and we'll send you a link to reset your password
                </CardDescription>
              </CardHeader>
              
              <div className="px-8 pb-8">
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input 
                          id="email" 
                          type="email" 
                          placeholder="your@email.com" 
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Sending Reset Link...' : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RequestResetPassword;
